import {IngredientTypes} from "./ingredients";
import {StationType} from "./station";

export type RecipeStepAction = 'chop' | 'cook' | 'merge';

export interface RecipeStep {
    action: RecipeStepAction;
    inputs: IngredientTypes[];
    output: IngredientTypes;
    station?: StationType;
}

export interface Recipe {
    dishType: IngredientTypes;
    displayName: string;
    steps: RecipeStep[];
}

/**
 * All recipes in the game.
 *
 * This registry is the single source of truth for recipe definitions.
 * Station processing rules (STATION_RECIPES) and merge rules in mix_actions.ts
 * must remain consistent with what is declared here.
 */
export const RECIPES: Recipe[] = [
    {
        dishType: IngredientTypes.TOMATO_SOUP,
        displayName: "Tomato Soup",
        steps: [
            {action: 'chop', inputs: [IngredientTypes.TOMATO], output: IngredientTypes.CHOPPED_TOMATO, station: StationType.CUTTING_BOARD},
            {action: 'cook', inputs: [IngredientTypes.CHOPPED_TOMATO], output: IngredientTypes.TOMATO_SOUP, station: StationType.STOVE},
        ],
    },
    {
        dishType: IngredientTypes.TOMATO_SALAD,
        displayName: "Tomato Salad",
        steps: [
            {action: 'chop', inputs: [IngredientTypes.TOMATO], output: IngredientTypes.CHOPPED_TOMATO, station: StationType.CUTTING_BOARD},
            {action: 'chop', inputs: [IngredientTypes.SALAD], output: IngredientTypes.CHOPPED_SALAD, station: StationType.CUTTING_BOARD},
            {action: 'merge', inputs: [IngredientTypes.CHOPPED_TOMATO, IngredientTypes.CHOPPED_SALAD], output: IngredientTypes.TOMATO_SALAD},
        ],
    },
    {
        dishType: IngredientTypes.ONION_SOUP,
        displayName: "Onion Soup",
        steps: [
            {action: 'chop', inputs: [IngredientTypes.ONION], output: IngredientTypes.CHOPPED_ONION, station: StationType.CUTTING_BOARD},
            {action: 'cook', inputs: [IngredientTypes.CHOPPED_ONION], output: IngredientTypes.ONION_SOUP, station: StationType.STOVE},
        ],
    },
    {
        dishType: IngredientTypes.MIXED_SALAD,
        displayName: "Mixed Salad",
        steps: [
            {action: 'chop', inputs: [IngredientTypes.TOMATO], output: IngredientTypes.CHOPPED_TOMATO, station: StationType.CUTTING_BOARD},
            {action: 'chop', inputs: [IngredientTypes.SALAD], output: IngredientTypes.CHOPPED_SALAD, station: StationType.CUTTING_BOARD},
            {action: 'chop', inputs: [IngredientTypes.ONION], output: IngredientTypes.CHOPPED_ONION, station: StationType.CUTTING_BOARD},
            {action: 'merge', inputs: [IngredientTypes.CHOPPED_TOMATO, IngredientTypes.CHOPPED_SALAD], output: IngredientTypes.TOMATO_SALAD},
            {action: 'merge', inputs: [IngredientTypes.TOMATO_SALAD, IngredientTypes.CHOPPED_ONION], output: IngredientTypes.MIXED_SALAD},
        ],
    },
    {
        dishType: IngredientTypes.VEGGIE_STEW,
        displayName: "Veggie Stew",
        steps: [
            {action: 'chop', inputs: [IngredientTypes.TOMATO], output: IngredientTypes.CHOPPED_TOMATO, station: StationType.CUTTING_BOARD},
            {action: 'chop', inputs: [IngredientTypes.ONION], output: IngredientTypes.CHOPPED_ONION, station: StationType.CUTTING_BOARD},
            {action: 'merge', inputs: [IngredientTypes.CHOPPED_TOMATO, IngredientTypes.CHOPPED_ONION], output: IngredientTypes.VEGGIE_MIX},
            {action: 'cook', inputs: [IngredientTypes.VEGGIE_MIX], output: IngredientTypes.VEGGIE_STEW, station: StationType.STOVE},
        ],
    },
];

/** Returns all dish types that can be ordered (the final output of each recipe). */
export function getAllDishTypes(): IngredientTypes[] {
    return RECIPES.map(r => r.dishType);
}

/**
 * Returns only the dish types that are feasible given the available ingredients and stations.
 * A recipe is feasible when every raw ingredient it needs is available from a basket, and
 * every station it requires is present in the kitchen.
 */
export function getFeasibleDishTypes(
    availableIngredients: Set<IngredientTypes>,
    availableStations: Set<StationType>
): IngredientTypes[] {
    return RECIPES
        .filter(recipe => {
            const rawIngredients = recipe.steps
                .filter(s => s.action === 'chop')
                .map(s => s.inputs[0]);
            if (!rawIngredients.every(i => availableIngredients.has(i))) return false;

            const neededStations = recipe.steps
                .filter(s => s.station !== undefined)
                .map(s => s.station!);
            if (!neededStations.every(s => availableStations.has(s))) return false;

            return true;
        })
        .map(r => r.dishType);
}

/** Returns the recipe for a given dish type, or undefined if not found. */
export function getRecipe(dishType: IngredientTypes): Recipe | undefined {
    return RECIPES.find(r => r.dishType === dishType);
}
